import { cx } from "../../lib/cx.js";
import { Layer } from "../_overlay/Layer.js";
import { useControllable } from "../../lib/useControllable.js";
import { breakpoints, mediaQueries } from "../../tokens/breakpoints.js";
import { useMediaQuery } from "../../lib/useMediaQuery.js";
import { useOptionalBrand } from "../ThemeRoot/BrandContext.js";
import { AppShellContext } from "./AppShellContext.js";
/* empty css             */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/AppShell/AppShell.tsx
/**
* Whether `element` is narrower than `width`, kept current with a ResizeObserver: the
* shell responds to its own width, which is the viewport's in the app. Without a
* ResizeObserver (jsdom), it falls back to the viewport.
*/
function useNarrowerThan(element, width) {
	const viewportNarrow = useMediaQuery(mediaQueries.below.md);
	const [narrow, setNarrow] = useState(null);
	useLayoutEffect(() => {
		if (!element || typeof ResizeObserver === "undefined") return;
		const measure = () => setNarrow(element.clientWidth < width);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, [element, width]);
	return narrow ?? viewportNarrow;
}
/**
* The page scaffold: a top bar, the navigation sidebar and the main column (an optional
* banner, then the scrolling content). From the `md` breakpoint (1024px) the sidebar is
* persistent and collapses to a rail; below it, it becomes a menu drawer over a scrim
* that traps focus and closes on Escape. A skip link leads to `main`.
*/
function AppShell({ topbar, sidebar, banner, children, brandPlacement: brandPlacementProp, sidebarSurface: sidebarSurfaceProp, sidebarCollapsed, defaultSidebarCollapsed = false, onSidebarCollapsedChange, menuOpen: menuOpenProp, defaultMenuOpen = false, onMenuOpenChange, menuLabel = "Menu", skipLinkLabel = "Skip to main content", mainId: mainIdProp, className, ...rest }) {
	const brand = useOptionalBrand();
	const brandPlacement = brandPlacementProp ?? brand?.pack.brandPlacement ?? "topbar";
	const sidebarSurface = brandPlacement === "sidebar" ? "inverse" : sidebarSurfaceProp ?? brand?.pack.sidebarSurface ?? "default";
	const id = useId();
	const sidebarId = `${id}sidebar`;
	const mainId = mainIdProp ?? `${id}main`;
	const [root, setRoot] = useState(null);
	const [drawerHost, setDrawerHost] = useState(null);
	const panelRef = useRef(null);
	const mainRef = useRef(null);
	const narrow = useNarrowerThan(root, breakpoints.md);
	const [collapsed, setCollapsed] = useControllable(sidebarCollapsed, defaultSidebarCollapsed, onSidebarCollapsedChange);
	const [menuOpenState, setMenuOpen] = useControllable(menuOpenProp, defaultMenuOpen, onMenuOpenChange);
	const menuOpen = narrow && menuOpenState;
	const wasNarrow = useRef(narrow);
	useEffect(() => {
		if (wasNarrow.current && !narrow && menuOpenState) setMenuOpen(false);
		wasNarrow.current = narrow;
	}, [
		narrow,
		menuOpenState,
		setMenuOpen
	]);
	const toggleSidebar = useCallback(() => {
		if (narrow) setMenuOpen(!menuOpenState);
		else setCollapsed(!collapsed);
	}, [
		narrow,
		menuOpenState,
		collapsed,
		setMenuOpen,
		setCollapsed
	]);
	const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);
	const state = useMemo(() => ({
		brandPlacement,
		sidebarSurface,
		narrow,
		collapsed: !narrow && collapsed,
		menuOpen,
		sidebarOpen: narrow ? menuOpen : !collapsed,
		sidebarId,
		toggleSidebar,
		closeMenu
	}), [
		brandPlacement,
		sidebarSurface,
		narrow,
		collapsed,
		menuOpen,
		sidebarId,
		toggleSidebar,
		closeMenu
	]);
	const skipToMain = (event) => {
		event.preventDefault();
		mainRef.current?.focus();
	};
	return /* @__PURE__ */ jsx(AppShellContext.Provider, {
		value: state,
		children: /* @__PURE__ */ jsxs("div", {
			...rest,
			ref: setRoot,
			className: cx("ds-app-shell", `ds-app-shell--brand-${brandPlacement}`, className),
			"data-narrow": narrow || void 0,
			children: [
				/* @__PURE__ */ jsx("a", {
					className: "ds-app-shell__skip",
					href: `#${mainId}`,
					onClick: skipToMain,
					children: skipLinkLabel
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "ds-app-shell__frame",
					children: [
						/* @__PURE__ */ jsx("div", {
							className: "ds-app-shell__topbar",
							children: topbar
						}),
						!menuOpen && /* @__PURE__ */ jsx("div", {
							id: sidebarId,
							className: "ds-app-shell__sidebar",
							"data-collapsed": state.collapsed || void 0,
							hidden: narrow,
							children: sidebar
						}),
						/* @__PURE__ */ jsxs("div", {
							className: "ds-app-shell__column",
							children: [banner && /* @__PURE__ */ jsx("div", {
								className: "ds-app-shell__banner",
								children: banner
							}), /* @__PURE__ */ jsx("main", {
								ref: mainRef,
								id: mainId,
								tabIndex: -1,
								className: "ds-app-shell__main",
								children: /* @__PURE__ */ jsx("div", {
									className: "ds-app-shell__workspace",
									children
								})
							})]
						})
					]
				}),
				/* @__PURE__ */ jsx("div", {
					ref: setDrawerHost,
					className: "ds-app-shell__drawer-host"
				}),
				menuOpen && drawerHost && /* @__PURE__ */ jsxs(Layer, {
					kind: "overlay",
					modal: true,
					container: drawerHost,
					className: "ds-app-shell__drawer",
					contentRef: panelRef,
					onDismiss: closeMenu,
					children: [/* @__PURE__ */ jsx("div", {
						className: "ds-app-shell__scrim",
						"aria-hidden": "true"
					}), /* @__PURE__ */ jsx("div", {
						ref: panelRef,
						id: sidebarId,
						role: "dialog",
						"aria-modal": "true",
						"aria-label": menuLabel,
						className: "ds-app-shell__panel",
						children: sidebar
					})]
				})
			]
		})
	});
}
//#endregion
export { AppShell };

//# sourceMappingURL=AppShell.js.map